import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useState } from 'react';
import useMap from '@alltrails/maps/hooks/useMap';
import type { Coordinates } from '@alltrails/types/AlgoliaResults';
import { useAskSuggestionsQuery } from '@/utils/askAllTrailsApi';

export const askSuggestionsDeadlineMs = 3000;

/**
 * Prompts the server generates for the middle of the map.
 */
const useAskSuggestions = (limit: number) => {
  const map = useMap();
  // Read once the map exists, so panning the map behind the empty state never asks again
  const [center, setCenter] = useState<Coordinates>();

  if (map && !center) {
    const { lat, lng } = map.getCenter().wrap();
    setCenter({ lat, lng });
  }

  const { currentData, isError } = useAskSuggestionsQuery(center ? { center, limit } : skipToken);
  const [isPastDeadline, setIsPastDeadline] = useState(false);
  const isWaiting = !currentData && !isError;

  useEffect(() => {
    if (!isWaiting) {
      return undefined;
    }

    const timeout = setTimeout(() => setIsPastDeadline(true), askSuggestionsDeadlineMs);

    return () => clearTimeout(timeout);
  }, [isWaiting]);

  if (isPastDeadline) {
    return { isLoading: false, suggestions: undefined };
  }

  return { isLoading: isWaiting, suggestions: currentData?.length ? currentData.slice(0, limit) : undefined };
};

export default useAskSuggestions;
